import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, Router, withHashLocation } from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService } from '@ngx-translate/core';

import { routes } from './app.routes';
import { orderReducers, OrderEffects } from './stores/order';
import { CartEffects, cartReducers } from './stores/cart';
import { EmployeeEffects, employeeReducers } from './stores/employee';
import { CouponEffects, couponReducers } from './stores/coupon';
import { CustomerEffects, customerReducers } from './stores/customer';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaymentEffects, paymentReducers } from './stores/payment';
import { LoyaltyEffects, loyaltyReducers } from './stores/loyalty';
import { ToastModule } from 'primeng/toast';
import { GlobalRulesEffects, globalRulesReducers } from './stores/global';
import { authInterceptor } from './cores/interceptors/auth.interceptor';
import { logHttpInterceptor } from './cores/logging';
import { provideLogging } from './cores/logging/logging.config';
import { ProductEffects, productReducers } from './stores/product';
import { PriceLiveEffects } from './stores/price-live.effect';
import { ActivationService } from './services/activation.service';
import { PermissionService } from './cores/permissions';
import { AUTH_STRATEGY, AppAuthService } from './cores/auth/app-auth-service';
import { AuthStrategyFactory } from './cores/auth/auth-factory';
import { AuthServiceStrategy } from './cores/auth/auth-service-strategy';
import { AuthType } from './shared/enums/AuthType.enum';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { initializeKeycloak } from './cores/auth/keycloak-init';
import {
  LanguageInitService,
  LocaleIdService,
  PosUserSettingsService,
  initializeLanguageFactory,
} from './cores/i18n';

function initializeApp(
  activationService: ActivationService,
  permissionService: PermissionService,
  authService: AppAuthService,
  router: Router,
) {
  return async (): Promise<void> => {
    if (!window?.electron?.isElectron) {
      return;
    }

    try {
      const response = await firstValueFrom(
        activationService.getActivationStatus(),
      );
      if (!response.activated) {
        await router.navigate(['/env-config']);
        return;
      }

      activationService.updateActivationStatus(true);
      if (authService.isLoggedIn()) {
        const permissionResult = await permissionService.loadPermissions(true);
        if (permissionResult === 'forbidden') {
          await router.navigate(['/unauthorized']);
          return;
        }
        if (
          permissionResult === 'unauthenticated' ||
          permissionResult === 'unavailable'
        ) {
          authService.clearSession();
          await router.navigate(['/login']);
          return;
        }
      }

      await router.navigate(['/login']);
    } catch {
      if (authService.isLoggedIn()) {
        authService.clearSession();
        await router.navigate(['/login']);
        return;
      }
      await router.navigate(['/login']);
    }
  };
}

export function authStrategyInitializer(
  factory: AuthStrategyFactory,
): AuthServiceStrategy {
  const isElectron = window?.electron?.isElectron || false;
  return factory.create(isElectron ? AuthType.BASIC : AuthType.BEARER);
}

const isElectronRuntime = window?.electron?.isElectron || false;

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: AUTH_STRATEGY,
      useFactory: authStrategyInitializer,
      deps: [AuthStrategyFactory],
    },
    MessageService,
    ConfirmationService,
    DialogService,
    provideTranslateService({
      defaultLanguage: 'vi',
    }),
    {
      provide: LOCALE_ID,
      useFactory: (localeIdService: LocaleIdService, settings: PosUserSettingsService) => {
        localeIdService.setFromLanguage(settings.getSavedLanguage())
        return localeIdService.localeId
      },
      deps: [LocaleIdService, PosUserSettingsService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [
        ActivationService,
        PermissionService,
        AppAuthService,
        Router,
      ],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguageFactory,
      deps: [LanguageInitService],
      multi: true,
    },
    // Web only — Electron/offline uses AuthType.BASIC against pos-offline-service, never Keycloak.
    ...(isElectronRuntime
      ? []
      : [
          {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
          },
        ]),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Electron production runs on file:// so hard reload needs hash routing.
    provideRouter(
      routes,
      ...(isElectronRuntime ? [withHashLocation()] : []),
    ),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([authInterceptor, logHttpInterceptor]),
    ),
    provideLogging(),
    provideAnimations(),
    importProvidersFrom(ToastModule),
    provideStore({
      cart: cartReducers,
      coupon: couponReducers,
      customer: customerReducers,
      employee: employeeReducers,
      loyalty: loyaltyReducers,
      order: orderReducers,
      payment: paymentReducers,
      product: productReducers,
      globalRules: globalRulesReducers,
    }),
    provideEffects([
      CartEffects,
      CouponEffects,
      CustomerEffects,
      EmployeeEffects,
      LoyaltyEffects,
      OrderEffects,
      ProductEffects,
      PriceLiveEffects,
      PaymentEffects,
      GlobalRulesEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: false,
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
  ],
};
